import type { TransactionsContext } from '@/queries/transactionsContext.query'
import type { StepContent, StepName } from '@/types/transactionsContext'
import type { Vault } from '@/types/vault'
import { useForm } from 'react-hook-form'
import { parseUnits } from 'viem'
import { AERA_PENDING_TAB_NAME } from './actionTab/aeraPendingTab'
import { BORING_REDEEM_TAB_NAME } from './actionTab/boringWaitForRedeemTab'
import { CLAIM_REWARDS_EXCEPTION_TAB_NAME } from './actionTab/claimRewardsExceptionTab'
import { GENERIC_PENDING_TAB_NAME } from './actionTab/genericPendingTab'
import { LIDO_WRAP_TAB_NAME } from './actionTab/lidoWrappingTab'
import { UMBRELLA_WITHDRAW_TAB_NAME } from './actionTab/umbrellaWithdrawTab'
import { YO_WAIT_FOR_REDEEM_TAB_NAME } from './actionTab/yoWaitForRedeemTab'

export function useTabsConfig({
  transactionsContext,
  vault,
}: { transactionsContext: TransactionsContext; vault: Vault }): Record<StepName, StepContent> {
  const { asset, name } = vault
  const idleAssets = transactionsContext?.asset.balanceNative ?? '0'
  const lockedValueInAssets = transactionsContext?.asset.positionValueInAsset ?? '0'
  const lpTokenBalance = transactionsContext?.lpToken.balanceNative ?? '0'

  const parseAssetValue = (rawAmount: string) => parseUnits(rawAmount, asset.decimals)

  const parseRedeemAmount = (rawAmount: string) => {
    if (lockedValueInAssets === '0') {
      return 0n
    }

    const lpTokenBalance = transactionsContext?.lpToken.balanceNative ?? '0'
    return (parseAssetValue(rawAmount) * BigInt(lpTokenBalance)) / BigInt(lockedValueInAssets)
  }

  const depositConfig: Omit<StepContent, 'form'> = {
    label: 'Supply',
    actionDescription: `Supply ${asset.symbol} to ${name}`,
    SuccessDescription: ({ amount }) => (
      <p>
        You supplied{' '}
        <span className="font-semibold text-accent">
          {amount} {asset.symbol}
        </span>{' '}
        to {name}
      </p>
    ),
    walletBalance: idleAssets,
    getParsedAmount: (rawAmount: string) => parseAssetValue(rawAmount).toString(),
    getParsedAssetAmount: (rawAmount: string) => parseAssetValue(rawAmount).toString(),
  }

  const withdrawPartialConfig: Omit<StepContent, 'form'> = {
    label: 'Withdraw',
    actionDescription: `Withdraw ${asset.symbol} from ${name}`,
    SuccessDescription: ({ amount }) => (
      <p>
        You withdrew{' '}
        <span className="font-semibold text-accent">
          {amount} {asset.symbol}
        </span>{' '}
        from {name}
      </p>
    ),
    walletBalance: lockedValueInAssets,
    getParsedAmount: (rawAmount: string) => parseRedeemAmount(rawAmount).toString(),
    getParsedAssetAmount: (rawAmount: string) => parseAssetValue(rawAmount).toString(),
  }

  return {
    deposit: {
      ...depositConfig,
      form: useForm(),
    },
    'request-deposit': {
      ...depositConfig,
      actionDescription: `Request supply ${asset.symbol} to ${name}`,
      SuccessDescription: ({ amount }) => (
        <p>
          You requested supply{' '}
          <span className="font-semibold text-accent">
            {amount} {asset.symbol}
          </span>{' '}
          to {name}
        </p>
      ),
      form: useForm(),
    },
    'claim-deposit': {
      form: useForm(),
      label: 'Supply',
      actionDescription: `Claim ${asset.symbol} deposit to ${name}`,
      SuccessDescription: ({ amount }) => (
        <p>
          You claimed deposit of{' '}
          <span className="font-semibold text-accent">
            {amount} {asset.symbol}
          </span>{' '}
          to {name}
        </p>
      ),
      walletBalance: idleAssets,
      getParsedAmount: (rawAmount: string) => parseAssetValue(rawAmount).toString(),
      getParsedAssetAmount: (rawAmount: string) => parseAssetValue(rawAmount).toString(),
    },
    'start-redeem-cooldown': {
      form: useForm(),
      label: 'Initialize cooldown',
      actionDescription: '',
      SuccessDescription: () => '',
      walletBalance: '0',
      getParsedAmount: () => '0',
      getParsedAssetAmount: () => '0',
    },
    redeem: {
      ...withdrawPartialConfig,
      form: useForm(),
    },
    'request-redeem': {
      ...withdrawPartialConfig,
      actionDescription: `Request withdraw ${asset.symbol} from ${name}`,
      SuccessDescription: ({ amount }) => (
        <p>
          You requested withdraw{' '}
          <span className="font-semibold text-accent">
            {amount} {asset.symbol}
          </span>{' '}
          from {name}
        </p>
      ),
      form: useForm(),
    },
    'claim-redeem': {
      form: useForm(),
      label: 'Claim withdraw',
      actionDescription: `Claim ${asset.symbol} withdraw from ${name}`,
      SuccessDescription: ({ amount }) => (
        <p>
          You claimed withdraw of{' '}
          <span className="font-semibold text-accent">
            {amount} {asset.symbol}
          </span>{' '}
          from {name}
        </p>
      ),
      walletBalance: lockedValueInAssets,
      getParsedAmount: (rawAmount: string) => parseRedeemAmount(rawAmount).toString(),
      getParsedAssetAmount: (rawAmount: string) => parseAssetValue(rawAmount).toString(),
    },
    'claim-rewards': {
      form: useForm(),
      label: 'Claim',
      actionDescription: `Claim rewards from ${name}`,
      SuccessDescription: () => <p>You claimed rewards from {name}</p>,
      walletBalance: '0',
      getParsedAmount: () =>
        transactionsContext?.rewards?.claimable?.length
          ? parseUnits(
              transactionsContext?.rewards.claimable[0].amount,
              transactionsContext?.rewards.claimable[0].asset.decimals
            ).toString()
          : '0',
      getParsedAssetAmount: (rawAmount: string) => parseAssetValue(rawAmount).toString(),
    },
    [CLAIM_REWARDS_EXCEPTION_TAB_NAME]: {
      form: useForm(),
      label: 'Claim externally',
      actionDescription: '',
      SuccessDescription: () => '',
      walletBalance: '0',
      getParsedAmount: () => '0',
      getParsedAssetAmount: () => '0',
    },
    [LIDO_WRAP_TAB_NAME]: {
      form: useForm(),
      label: 'Transaction',
      actionDescription: 'Wrap/unwrap stETH',
      SuccessDescription: () => 'Wrap/unwrap transaction successful',
      walletBalance: lpTokenBalance,
      getParsedAmount: (rawAmount: string) => parseAssetValue(rawAmount).toString(),
      getParsedAssetAmount: (rawAmount: string) => parseAssetValue(rawAmount).toString(),
    },
    [UMBRELLA_WITHDRAW_TAB_NAME]: {
      ...withdrawPartialConfig,
      form: useForm(),
    },
    [BORING_REDEEM_TAB_NAME]: {
      form: useForm(),
      label: 'Pending',
      actionDescription: '',
      SuccessDescription: () => '',
      walletBalance: lpTokenBalance,
      getParsedAmount: (rawAmount: string) => parseAssetValue(rawAmount).toString(),
      getParsedAssetAmount: (rawAmount: string) => parseAssetValue(rawAmount).toString(),
    },
    [YO_WAIT_FOR_REDEEM_TAB_NAME]: {
      form: useForm(),
      label: 'Pending',
      actionDescription: '',
      SuccessDescription: () => '',
      walletBalance: lpTokenBalance,
      getParsedAmount: (rawAmount: string) => parseAssetValue(rawAmount).toString(),
      getParsedAssetAmount: (rawAmount: string) => parseAssetValue(rawAmount).toString(),
    },
    [AERA_PENDING_TAB_NAME]: {
      form: useForm(),
      label: 'Pending',
      actionDescription: '',
      SuccessDescription: () => '',
      walletBalance: lpTokenBalance,
      getParsedAmount: (rawAmount: string) => parseAssetValue(rawAmount).toString(),
      getParsedAssetAmount: (rawAmount: string) => parseAssetValue(rawAmount).toString(),
    },
    [GENERIC_PENDING_TAB_NAME]: {
      form: useForm(),
      label: 'Pending',
      actionDescription: '',
      SuccessDescription: () => '',
      walletBalance: lpTokenBalance,
      getParsedAmount: (rawAmount: string) => parseAssetValue(rawAmount).toString(),
      getParsedAssetAmount: (rawAmount: string) => parseAssetValue(rawAmount).toString(),
    },
  } as const
}

export type TabsConfig = ReturnType<typeof useTabsConfig>
export type TabConfig = TabsConfig[keyof TabsConfig]
